import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, PlayCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { ProgramDrill } from '@/types/training-program';
import { cn } from '@/lib/utils';

export type DrillCardVariant = 'required' | 'optional' | 'collapsed' | 'expanded';

interface DrillCardProps {
  drill: ProgramDrill;
  variant?: DrillCardVariant;
  onStart?: (drillId: string) => void;
  onPracticeLater?: (drillId: string) => void;
  onSwap?: (drillId: string) => void;
  showActions?: boolean;
}

const formatAssignedReps = (drill: ProgramDrill) => {
  const { sets, reps_per_set, rest_seconds } = drill.assigned_reps;
  if (sets > 0 && reps_per_set > 0) {
    const rest = rest_seconds > 0 ? ` • Rest ${rest_seconds}s` : '';
    return `${sets} sets of ${reps_per_set}${rest}`;
  }
  if (reps_per_set > 0) {
    return `${reps_per_set} reps`;
  }
  return 'Reps will be assigned in your plan.';
};

export function DrillCard({
  drill,
  variant = 'expanded',
  onStart,
  onPracticeLater,
  onSwap,
  showActions = false,
}: DrillCardProps) {
  const [expanded, setExpanded] = useState(variant !== 'collapsed');
  const isCollapsedVariant = variant === 'collapsed';
  const isOptional = variant === 'optional' || !drill.is_required;
  const borderAccent = isOptional ? 'border-l-[#F9C94E]/50' : 'border-l-[#196644]';

  const shouldShowExpanded = useMemo(() => {
    if (variant === 'expanded') return true;
    if (variant === 'collapsed') return false;
    return expanded;
  }, [expanded, variant]);

  return (
    <Card className={cn('shadow-[0_2px_8px_rgba(13,56,42,0.08)]', 'border-l-4', borderAccent)}>
      <CardHeader
        className={cn('pb-2 cursor-pointer', isCollapsedVariant && 'cursor-default')}
        onClick={() => {
          if (isCollapsedVariant) return;
          setExpanded((prev) => !prev);
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-lg text-[#0D382A]">{drill.drill_name}</CardTitle>
              {/* UX-AUDIT-V3-004: Required badge for required drills */}
              {variant === 'required' && (
                <span className="bg-gradient-to-r from-[#196644] to-[#1B7D52] text-white text-xs px-2 py-0.5 rounded-full">
                  Required
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">{drill.focus_area}</p>
          </div>
          {!isCollapsedVariant && (
            <div className="text-gray-400">
              {shouldShowExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          )}
        </div>
      </CardHeader>

      {shouldShowExpanded && (
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm text-gray-500">Why this helps your program</p>
            <p className="text-sm text-[#0D382A] mt-1">{drill.why_this_helps}</p>
          </div>

          <div className="bg-gradient-to-r from-[#0D382A] to-[#196644] text-white rounded-xl p-4">
            <p className="text-xs uppercase tracking-wide text-white/70">Swing Thought</p>
            <p className="text-base font-semibold mt-1">{drill.swing_thought || 'Stay focused on your swing thought.'}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">How to do the drill</p>
            <ol className="mt-2 space-y-2">
              {drill.how_to_perform.map((step, index) => (
                <li key={index} className="flex gap-2 text-sm text-[#0D382A]">
                  <span className="h-5 w-5 rounded-full bg-[#196644]/10 text-[#196644] flex items-center justify-center text-xs">
                    {index + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="bg-[#F9C94E]/10 border border-[#F9C94E]/30 rounded-xl p-3">
            <p className="text-sm text-[#0D382A] font-medium">Assigned Reps</p>
            <p className="text-sm text-gray-700 mt-1">{formatAssignedReps(drill)}</p>
          </div>

          {drill.what_to_look_for.length > 0 && (
            <div>
              <p className="text-sm text-gray-500">What to look for</p>
              <ul className="mt-2 space-y-2">
                {drill.what_to_look_for.map((item, index) => (
                  <li key={index} className="text-sm text-[#0D382A] flex gap-2">
                    <span className="text-[#196644]">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {drill.common_mistakes && drill.common_mistakes.length > 0 && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-3">
              <p className="text-sm text-red-700 font-medium">Common Mistakes</p>
              <ul className="mt-2 space-y-2">
                {drill.common_mistakes.map((item, index) => (
                  <li key={index} className="text-sm text-red-700">• {item}</li>
                ))}
              </ul>
            </div>
          )}

          {drill.video_url && (
            <div className="rounded-xl border border-dashed border-gray-300 p-4 flex items-center gap-3 text-gray-500">
              <PlayCircle className="w-6 h-6" />
              <span className="text-sm">Video available</span>
            </div>
          )}

          {drill.when_to_stop && (
            <div className="bg-[#196644]/10 border border-[#196644]/20 rounded-xl p-3">
              <p className="text-sm text-[#196644] font-medium">When to Stop</p>
              <p className="text-sm text-[#0D382A] mt-1">{drill.when_to_stop}</p>
            </div>
          )}

          {drill.mobility_modifier && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
              <p className="text-sm text-blue-700 font-medium">Mobility Modifier</p>
              <p className="text-sm text-blue-700 mt-1">{drill.mobility_modifier}</p>
            </div>
          )}

          {showActions && (
            <div className="flex flex-wrap gap-2 pt-2">
              {onStart && (
                <Button onClick={() => onStart(drill.drill_id)}>Start Drill</Button>
              )}
              {onPracticeLater && (
                <Button variant="secondary" onClick={() => onPracticeLater(drill.drill_id)}>
                  Practice Later
                </Button>
              )}
              {onSwap && (
                <Button variant="outline" onClick={() => onSwap(drill.drill_id)}>
                  Swap Drill
                </Button>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
